import { ArrowUpDown } from 'lucide-react';
import type { JSX, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react';

import { cn } from '../lib/cn';

export interface TableProps {
  children: ReactNode;
  className?: string;
}

/**
 * A `<table>` inside a rounded, bordered, soft-shadow container with its own
 * scroll region — the wrapper `TableHeader` cells stick to via `sticky top-0`.
 */
export function Table({ children, className }: TableProps): JSX.Element {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm',
        className,
      )}
    >
      <div className="overflow-auto">
        <table className="w-full border-collapse text-left text-sm">{children}</table>
      </div>
    </div>
  );
}

export interface TableHeaderProps extends Omit<
  ThHTMLAttributes<HTMLTableCellElement>,
  'className'
> {
  children: ReactNode;
  /** Optional sort indicator only \u2014 no sorting logic lives here. */
  sortDirection?: 'asc' | 'desc' | null;
  className?: string;
}

/** A `<th scope="col">` column header, with an optional sort direction icon. */
export function TableHeader({
  children,
  sortDirection,
  className,
  ...props
}: TableHeaderProps): JSX.Element {
  return (
    <th
      scope="col"
      className={cn(
        'sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-4 py-3 text-xs first:pl-6 last:pr-6 font-medium uppercase tracking-wide text-slate-500 backdrop-blur-sm',
        className,
      )}
      {...props}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortDirection !== undefined ? (
          <ArrowUpDown
            aria-hidden="true"
            className={cn('size-3.5', sortDirection === null ? 'text-slate-300' : 'text-slate-600')}
          />
        ) : null}
      </span>
    </th>
  );
}

export interface TableBodyProps {
  children: ReactNode;
  className?: string;
}

/** A `<tbody>` wrapper. Renders correctly with zero `TableRow` children. */
export function TableBody({ children, className }: TableBodyProps): JSX.Element {
  return <tbody className={className}>{children}</tbody>;
}

export interface TableRowProps {
  children: ReactNode;
  className?: string;
}

/** A `<tr>` with the zebra/hover treatment for body rows. */
export function TableRow({ children, className }: TableRowProps): JSX.Element {
  return (
    <tr
      className={cn(
        'border-b border-slate-100 transition-colors duration-150 last:border-0 even:bg-slate-50/60 hover:bg-slate-100',
        className,
      )}
    >
      {children}
    </tr>
  );
}

export interface TableCellProps extends Omit<TdHTMLAttributes<HTMLTableCellElement>, 'className'> {
  children?: ReactNode;
  className?: string;
}

/** A `<td>` cell with the comfortable padding shared across web-admin tables. */
export function TableCell({ children, className, ...props }: TableCellProps): JSX.Element {
  return (
    <td className={cn('px-4 py-3 text-slate-700 first:pl-6 last:pr-6', className)} {...props}>
      {children}
    </td>
  );
}
